import { KanguroPointCard } from "@components/delivery/KanguroPoint.card";
import { Header } from "@components/Header";
import { CurrentKanguroPointViewModel } from "@viewModels/CurrentKanguroPoint.viewModel";
import React, { useEffect, useMemo } from "react";
import { FlatList, Image, StyleSheet, View } from "react-native";
import { ProgressBar, Text } from "react-native-paper";
import { ListCurrentKanguroPointsResource } from "./resources/ListCurrentKanguroPoints.resource";
import { theme } from "@config/Theme";
import { useTranslation } from "react-i18next";
import UserParcelsEmpty from "@assets/img/user-parcels-empty.png";
import Geolocation from "@react-native-community/geolocation";

interface Props {
  openDelivery: (currentKanguroPoint: CurrentKanguroPointViewModel) => void;
}

interface Corrdinates {
  latitude: number;
  longitude: number;
}

export const ListCurrentKanguroPointsFeature = ({ openDelivery }: Props) => {
  const { t } = useTranslation("recipient");

  // states
  const [coordinates, setCoordinates] = React.useState<Corrdinates | null>(
    null
  );
  const [pageNumber, setPageNumber] = React.useState<number>(0);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [hasMoreData, setHasMoreData] = React.useState<boolean>(false);
  const [kanguroPoints, setKanguroPoints] = React.useState<
    CurrentKanguroPointViewModel[]
  >([]);

  const parcelNumber = useMemo(() => {
    return kanguroPoints
      .map((kanguroPoint) => kanguroPoint.parcelsCount)
      .reduce((a, b) => a + b, 0);
  }, [kanguroPoints]);

  // Functions
  async function getFirstChunckOfData() {
    setPageNumber(0);
    setIsLoading(true);
    ListCurrentKanguroPointsResource.list(
      coordinates?.latitude ?? 0,
      coordinates?.longitude ?? 0,
      0
    )
      .then((response) => {
        setKanguroPoints(response);
        setPageNumber(1); // after getting the first chunck of data, set the page number to 1
        setHasMoreData(response.length >= 10);
      })
      .catch((err) => {
        __DEV__ && console.log("error while loading more data", err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  function loadMoreData() {
    if (!hasMoreData || isLoading) return;
    setIsLoading(true);
    ListCurrentKanguroPointsResource.list(
      coordinates!.latitude,
      coordinates!.longitude,
      pageNumber
    )
      .then((response) => {
        setKanguroPoints((state) => [...state, ...response]);
        setPageNumber(pageNumber + 1);
        setHasMoreData(response.length >= 10);
      })
      .catch((err) => {
        __DEV__ && console.log("error while loading more data", err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  // effects
  useEffect(() => {
    Geolocation.getCurrentPosition(
      (info) => {
        const data: Corrdinates = {
          latitude: info.coords.latitude,
          longitude: info.coords.longitude,
        };
        setCoordinates(data);
      },
      (err) => __DEV__ && console.log(err),
      // enableHighAccuracy : true will use gps , false will use wifi
      // maximumAge: enable used cached data
      { enableHighAccuracy: false, maximumAge: 10000 }
    );
  }, []);

  // after getting the corrdinates we can fetch our first chunck of data based on the cordinates
  useEffect(() => {
    getFirstChunckOfData();
  }, [coordinates]);

  // rendering components
  const emptyViewComponent = () => {
    return (
      <>
        <Header
          greeting
          title={t("listCurrentKanguroPoints_feature_title")!}
          backgroundColor={theme.colors.gray}
        />
        <View style={styles.emptyView}>
          <View>
            <Text variant="bodyMedium" style={{ paddingHorizontal: 20 }}>
              {t("listCurrentKanguroPoints_feature_description")!}
            </Text>
          </View>
          <Image
            source={UserParcelsEmpty}
            style={styles.userParcelsEmptyImage}
          />
        </View>
      </>
    );
  };

  // will show the loader only for first chunck of data
  if (isLoading && pageNumber === 0) {
    return <ProgressBar indeterminate />;
  }

  // if there are no parcels will render empty view
  if (parcelNumber === 0) {
    return emptyViewComponent();
  }

  return (
    <>
      <Header
        greeting
        subtitle={t("listCurrentKanguroPoints_feature_subtitle")!}
        backgroundColor={theme.colors.gray}
      />
      <FlatList
        data={kanguroPoints}
        keyExtractor={(kanguroPoint) => kanguroPoint.id}
        onEndReached={loadMoreData}
        onEndReachedThreshold={0.95}
        showsVerticalScrollIndicator={false}
        style={styles.listStyle}
        contentContainerStyle={styles.listContentStyle}
        refreshing={isLoading}
        onRefresh={async () => await getFirstChunckOfData()}
        renderItem={({ item }) => (
          <View key={item.id} style={{ marginTop: 15 }}>
            <KanguroPointCard
              onPress={() => openDelivery(item)}
              kanguroPoint={item}
              type={"recipient"}
            />
          </View>
        )}
      />
    </>
  );
};

const styles = StyleSheet.create({
  emptyView: {
    flex: 1,
    justifyContent: "space-between",
    paddingTop: 20,
    backgroundColor: theme.colors.gray,
  },
  userParcelsEmptyImage: {
    bottom: 0,
    width: "100%",
    height: "100%",
    resizeMode: "contain",
  },
  listStyle: {
    flex: 1,
  },
  listContentStyle: {
    paddingHorizontal: 20,
    position: "relative",
    backgroundColor: theme.colors.gray,
  },
});
